import React, { useEffect, useState } from "react";
import SetupStep from "@/components/SetupStep";
import api from "@/lib/api";
import { toast } from "sonner";
import { Store, Plus } from "lucide-react";

/**
 * /setup/vendors — SECOND focused mini-page. Shows a lightweight "add
 * one vendor now, add more later" form so the user gets a concrete win
 * before advancing to Staples. Type selector + name + optional
 * suburb/city; nothing else — the full Vendors page is where power
 * users tune everything else.
 */
const TYPES = [
  "Supermarket", "Grocery", "Butcher", "Bakery",
  "Farmers Market", "Pharmacy", "Hardware", "Other",
];

export default function SetupVendors() {
  const [name, setName] = useState("");
  const [type, setType] = useState("Supermarket");
  const [locality, setLocality] = useState("");
  const [existing, setExisting] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/vendors");
        setExisting(Array.isArray(data) ? data.length : (data?.total || 0));
      } catch { /* not fatal */ }
    })();
  }, []);

  const saveContinue = async () => {
    if (!name.trim()) return; // saveContinue was called with empty form — allow skipping
    setSaving(true);
    try {
      await api.post("/vendors", {
        name: name.trim(),
        type,
        address: locality.trim() || null,
      });
      toast.success(`${name.trim()} added.`);
    } catch (err) {
      toast.error("Couldn't add that vendor. You can still continue.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SetupStep
      step={2}
      eyebrow="FIRST LIGHT · STEP 3 OF 5"
      title="Save your local shops."
      subtitle="Add one of the shops your family actually uses — supermarket, butcher, bakery. When you refill groceries later, HearthHouse will remember where you bought them."
      onSaveContinue={saveContinue}
      nextRoute="/setup/staples"
      busy={saving}
      skipLabel={existing > 0 ? "I've got vendors already — continue" : "Skip for now"}
    >
      <div className="flex items-center gap-3 text-hearth-mute mb-6">
        <div className="w-10 h-10 rounded-full bg-hearth-cream grid place-items-center">
          <Store size={18} className="text-hearth-clayDark" />
        </div>
        <div className="text-sm">
          {existing > 0
            ? `You already have ${existing} vendor${existing === 1 ? "" : "s"} saved. Add another below, or continue.`
            : "This takes 20 seconds. Or skip and add them any time from the Vendors page."}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2">
          <span className="caption text-hearth-mute tracking-widest">Shop name</span>
          <input
            data-testid="setup-vendor-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Woolworths Bondi Junction"
            className="mt-1.5 w-full h-11 rounded-xl border border-hearth-line px-4 bg-hearth-cream/40 focus:outline-none focus:border-hearth-clay/60"
          />
        </label>
        <label className="block">
          <span className="caption text-hearth-mute tracking-widest">Type</span>
          <select
            data-testid="setup-vendor-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="mt-1.5 w-full h-11 rounded-xl border border-hearth-line px-4 bg-hearth-cream/40 focus:outline-none focus:border-hearth-clay/60"
          >
            {TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="caption text-hearth-mute tracking-widest">Suburb (optional)</span>
          <input
            data-testid="setup-vendor-locality"
            type="text"
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
            placeholder="e.g. Bondi"
            className="mt-1.5 w-full h-11 rounded-xl border border-hearth-line px-4 bg-hearth-cream/40 focus:outline-none focus:border-hearth-clay/60"
          />
        </label>
      </div>

      <p className="mt-4 text-xs text-hearth-mute italic flex items-center gap-2">
        <Plus size={12} /> More vendors, hours, phone numbers and photos live on the Vendors page.
      </p>
    </SetupStep>
  );
}
